import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import {
  Settings2, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
  Copy, Save, Play, Loader2, Server,
} from "lucide-react";

interface ConfigData {
  isEnabled: boolean;
  whmcsUrl: string | null;
  apiIdentifier: string | null;
  apiSecret: string | null;
  hasApiSecret?: boolean;
  startDate: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  ticketsCount: number;
  updatedAt: string;
  qualityAutoEnabled?: boolean;
  slaFirstResponseHours?: number;
  slaResolutionHours?: number;
  slaMaxReplies?: number;
  slaMaxTransfers?: number;
}

interface TestResult {
  ok: boolean;
  whmcsVersion?: string;
  ticketsAccessible?: boolean;
  totalTickets?: number;
  responseTimeMs?: number;
  errorType?: string;
  errorMessage?: string;
}

const ERROR_HELP: Record<string, { title: string; hint: string }> = {
  invalid_credentials: { title: "Invalid credentials", hint: "Double-check the API Identifier and Secret in WHMCS → Setup → API Credentials." },
  ip_not_whitelisted:  { title: "IP not whitelisted", hint: "Add this server's IP to WHMCS → Setup → API Credentials → Allowed IP addresses." },
  url_unreachable:     { title: "URL unreachable", hint: "Check the WHMCS URL — is it correct and reachable from this server?" },
  permission_denied:   { title: "Permission denied", hint: "The API user needs GetTickets, GetTicket permissions in WHMCS." },
  timeout:             { title: "Timeout", hint: "WHMCS did not respond in 15s. Try again or check WHMCS server health." },
  unknown:             { title: "Unknown error", hint: "Check the message below and WHMCS logs for details." },
};

function fmt(dt: string | null): string {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleString();
}

function ago(dt: string | null): string {
  if (!dt) return "never";
  const diff = Date.now() - new Date(dt).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function WhmcsConfig() {
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const [form, setForm] = useState({
    isEnabled: false,
    whmcsUrl: "",
    apiIdentifier: "",
    apiSecret: "",
    startDate: "",
    secretDirty: false,
    qualityAutoEnabled: true,
    slaFirstResponseHours: 4,
    slaResolutionHours: 48,
    slaMaxReplies: 5,
    slaMaxTransfers: 2,
  });

  const [serverIp, setServerIp] = useState<string>("");

  const load = () => {
    setLoading(true);
    api.get("/whmcs/config").then(r => {
      const c: ConfigData = r.data;
      setConfig(c);
      setForm({
        isEnabled: !!c.isEnabled,
        whmcsUrl: c.whmcsUrl || "",
        apiIdentifier: c.apiIdentifier || "",
        apiSecret: c.hasApiSecret ? "••••••••••••" : "",
        startDate: c.startDate ? c.startDate.slice(0, 10) : "",
        secretDirty: false,
        qualityAutoEnabled: c.qualityAutoEnabled ?? true,
        slaFirstResponseHours: c.slaFirstResponseHours ?? 4,
        slaResolutionHours: c.slaResolutionHours ?? 48,
        slaMaxReplies: c.slaMaxReplies ?? 5,
        slaMaxTransfers: c.slaMaxTransfers ?? 2,
      });
    }).catch(() => setConfig(null)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Get the actual server outbound IP (not the browser's)
  useEffect(() => {
    api.get("/whmcs/server-ip")
      .then(r => setServerIp(r.data?.ip || ""))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body: any = {
        isEnabled: form.isEnabled,
        whmcsUrl: form.whmcsUrl.trim(),
        apiIdentifier: form.apiIdentifier.trim(),
        startDate: form.startDate || null,
        qualityAutoEnabled: form.qualityAutoEnabled,
        slaFirstResponseHours: form.slaFirstResponseHours,
        slaResolutionHours: form.slaResolutionHours,
        slaMaxReplies: form.slaMaxReplies,
        slaMaxTransfers: form.slaMaxTransfers,
      };
      if (form.secretDirty && form.apiSecret) body.apiSecret = form.apiSecret;
      await api.put("/whmcs/config", body);
      toast.success("Settings saved");
      // Auto-test after save
      await runTest();
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const [savingSla, setSavingSla] = useState(false);
  const saveSlaOnly = async () => {
    setSavingSla(true);
    try {
      await api.put("/whmcs/config", {
        qualityAutoEnabled: form.qualityAutoEnabled,
        slaFirstResponseHours: form.slaFirstResponseHours,
        slaResolutionHours: form.slaResolutionHours,
        slaMaxReplies: form.slaMaxReplies,
        slaMaxTransfers: form.slaMaxTransfers,
      });
      toast.success("Thresholds saved · recomputing all tickets…");
      // Auto-recompute so new thresholds apply immediately to existing tickets
      try {
        const r = await api.post("/whmcs/recompute-all-quality");
        const n = r?.data?.recomputed ?? r?.data?.updated ?? "";
        toast.success(`Recomputed ${n} tickets ✓`);
      } catch (recErr: any) {
        toast.error("Saved, but recompute failed: " + (recErr?.response?.data?.message || recErr.message));
      }
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save thresholds");
    } finally { setSavingSla(false); }
  };

  const runTest = async (): Promise<TestResult | null> => {
    setTesting(true);
    setTestResult(null);
    try {
      const body: any = {};
      if (form.whmcsUrl) body.whmcsUrl = form.whmcsUrl;
      if (form.apiIdentifier) body.apiIdentifier = form.apiIdentifier;
      if (form.secretDirty && form.apiSecret) body.apiSecret = form.apiSecret;
      const r = await api.post("/whmcs/test-connection", body);
      setTestResult(r.data);
      if (r.data.ok) toast.success("Connection OK");
      else toast.error(ERROR_HELP[r.data.errorType || "unknown"]?.title || "Connection failed");
      return r.data;
    } catch (err: any) {
      toast.error("Test failed");
      return null;
    } finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/whmcs/sync-now");
      toast.success(`Sync complete: ${r.data.synced} tickets`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Sync failed");
    } finally { setSyncing(false); }
  };

  const copyIp = () => {
    if (!serverIp) return;
    navigator.clipboard.writeText(serverIp);
    toast.success("Copied");
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;

  const isConfigured = !!(config?.whmcsUrl && config?.apiIdentifier && config?.hasApiSecret);
  const statusBadge = () => {
    if (!config?.isEnabled) return { color: "gray", icon: "⚪", label: "Disabled" };
    if (!isConfigured) return { color: "blue", icon: "🔵", label: "Not configured" };
    if (config.lastSyncStatus === "error") return { color: "red", icon: "🔴", label: "Disconnected" };
    if (config.lastSyncStatus === "ok" || config.lastSyncStatus === "in_progress") return { color: "green", icon: "🟢", label: "Connected" };
    if (config.lastSyncStatus === "partial") return { color: "yellow", icon: "🟡", label: "Warning" };
    return { color: "blue", icon: "🔵", label: "Ready" };
  };
  const status = statusBadge();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <Server className="text-blue-500" size={28}/>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">WHMCS Integration</h1>
          <p className="text-sm text-gray-500">Configure the connection to your WHMCS installation</p>
        </div>
      </div>

      {/* Status Badge */}
      <div className={`rounded-2xl border p-4 mb-5 ${
        status.color === "green" ? "bg-green-50 border-green-200" :
        status.color === "red" ? "bg-red-50 border-red-200" :
        status.color === "yellow" ? "bg-amber-50 border-amber-200" :
        status.color === "gray" ? "bg-gray-50 border-gray-200" :
        "bg-blue-50 border-blue-200"
      }`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{status.icon}</span>
          <div className="flex-1">
            <div className={`text-lg font-semibold ${
              status.color === "green" ? "text-green-800" :
              status.color === "red" ? "text-red-800" :
              status.color === "yellow" ? "text-amber-800" :
              status.color === "gray" ? "text-gray-700" :
              "text-blue-800"
            }`}>{status.label}</div>
            <div className="text-xs text-gray-500">
              {config?.lastSyncAt ? `Last sync ${ago(config.lastSyncAt)}` : "No sync yet"}
              {config?.ticketsCount ? ` · ${config.ticketsCount} tickets stored` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Connection card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">Connection</h2>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.isEnabled}
              onChange={e => setForm(f => ({...f, isEnabled: e.target.checked}))}
              className="rounded"/>
            Enabled
          </label>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WHMCS URL</label>
            <input value={form.whmcsUrl}
              onChange={e => setForm(f => ({...f, whmcsUrl: e.target.value}))}
              placeholder="https://your-whmcs.example.com/whmcs"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">API Identifier</label>
            <input value={form.apiIdentifier}
              onChange={e => setForm(f => ({...f, apiIdentifier: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              API Secret {config?.hasApiSecret && !form.secretDirty && <span className="text-gray-400">(saved — enter to change)</span>}
            </label>
            <input type="password" value={form.apiSecret}
              onChange={e => setForm(f => ({...f, apiSecret: e.target.value, secretDirty: true}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
          </div>
          {serverIp && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-500 mb-1">This server's IP (whitelist this in WHMCS):</div>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-gray-800">{serverIp}</code>
                <button onClick={copyIp} className="text-gray-400 hover:text-gray-700"><Copy size={14}/></button>
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={runTest} disabled={testing}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              {testing ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>}
              Test Connection
            </button>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 ml-auto">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              Save Settings
            </button>
          </div>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`mt-4 rounded-lg p-4 border ${testResult.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-start gap-3">
              {testResult.ok
                ? <CheckCircle2 size={20} className="text-green-600 flex-shrink-0 mt-0.5"/>
                : <XCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5"/>}
              <div className="flex-1 min-w-0">
                {testResult.ok ? (
                  <>
                    <div className="text-green-800 font-semibold text-sm">Connection Successful</div>
                    <div className="text-xs text-gray-600 mt-2 space-y-1">
                      {testResult.totalTickets !== undefined && <div>Tickets accessible: <b>{testResult.totalTickets.toLocaleString()}</b> in WHMCS</div>}
                      {testResult.responseTimeMs !== undefined && <div>Response time: <b>{testResult.responseTimeMs} ms</b></div>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-red-800 font-semibold text-sm">
                      {ERROR_HELP[testResult.errorType || "unknown"]?.title || "Connection Failed"}
                    </div>
                    <div className="text-xs text-red-700 mt-1">{testResult.errorMessage}</div>
                    <div className="text-xs text-gray-600 mt-2">
                      💡 {ERROR_HELP[testResult.errorType || "unknown"]?.hint}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Sync</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fetch tickets from date</label>
            <input type="date" value={form.startDate}
              onChange={e => setForm(f => ({...f, startDate: e.target.value}))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
            <p className="text-xs text-gray-500 mt-1">Only tickets created on or after this date will be pulled</p>
          </div>
          <div className="text-xs text-gray-600 space-y-1 bg-gray-50 rounded-lg p-3">
            <div>Auto-sync: <b>every hour</b> (at minute 3)</div>
            <div>Last sync: <b>{fmt(config?.lastSyncAt || null)}</b> ({ago(config?.lastSyncAt || null)})</div>
            <div>Status: <b className={
              config?.lastSyncStatus === "ok" ? "text-green-700" :
              config?.lastSyncStatus === "error" ? "text-red-700" :
              config?.lastSyncStatus === "partial" ? "text-amber-700" : "text-gray-500"
            }>{config?.lastSyncStatus || "never synced"}</b></div>
            {config?.lastSyncError && <div className="text-red-600">Error: {config.lastSyncError}</div>}
            <div>Tickets stored: <b>{config?.ticketsCount || 0}</b></div>
          </div>
          <button onClick={syncNow} disabled={syncing || !config?.isEnabled || !isConfigured}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
            {syncing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
            Sync Now
          </button>
        </div>
      </div>

      {/* Quality SLA card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mt-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">Quality SLA Thresholds</h2>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.qualityAutoEnabled}
              onChange={e => setForm(f => ({...f, qualityAutoEnabled: e.target.checked}))}
              className="rounded"/>
            Auto-compute
          </label>
        </div>
        <p className="text-xs text-gray-500 mb-4">Tickets that meet all thresholds are graded 🟢 Excellent. Missing thresholds move down to Good / Fair / Poor.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              First Response Time (hours): <b>{form.slaFirstResponseHours}h</b>
            </label>
            <input type="range" min="0.5" max="24" step="0.5"
              value={form.slaFirstResponseHours}
              onChange={e => setForm(f => ({...f, slaFirstResponseHours: Number(e.target.value)}))}
              className="w-full"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Resolution Time (hours): <b>{form.slaResolutionHours}h</b>
            </label>
            <input type="range" min="4" max="336" step="4"
              value={form.slaResolutionHours}
              onChange={e => setForm(f => ({...f, slaResolutionHours: Number(e.target.value)}))}
              className="w-full"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Max Replies: <b>{form.slaMaxReplies}</b>
            </label>
            <input type="range" min="2" max="20" step="1"
              value={form.slaMaxReplies}
              onChange={e => setForm(f => ({...f, slaMaxReplies: Number(e.target.value)}))}
              className="w-full"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Max Department Transfers: <b>{form.slaMaxTransfers}</b>
            </label>
            <input type="range" min="0" max="5" step="1"
              value={form.slaMaxTransfers}
              onChange={e => setForm(f => ({...f, slaMaxTransfers: Number(e.target.value)}))}
              className="w-full"/>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
          <button onClick={saveSlaOnly} disabled={savingSla}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
            {savingSla ? <Loader2 size={14} className="animate-spin"/> : <span>💾</span>}
            Save Thresholds
          </button>
        </div>
      </div>
    </div>
  );
}
